import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { GENDER } from '@feature/dashboard/senders/constant/upsert-sender.constant';
import { SenderService } from '@feature/dashboard/senders/services/sender.service';
import { SenderListInterface } from '@feature/dashboard/senders/types/sender-list';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { REGEX_CONSTANT } from '@sharedModule/constants/pattern-regex.constant';
import { HasPlatformAccessDirective } from '@sharedModule/directives/has-platform-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { CountryInfo } from '@sharedModule/models/country.model';
import { loadDestinationCountry } from '@sharedModule/store/destination-country/destination-country.actions';
import { selectDestinationCountry } from '@sharedModule/store/destination-country/destination-country.selector';
import {
  getSenderDetailsFromId,
  getCountryInfoFromCountryCode,
} from '@sharedModule/utils/array.utils';
import { removeEmptyStrings } from '@sharedModule/utils/form.utils';
import { TransferDetailsComponent } from '@sharedModule/components/transfer-details/transfer-details.component';
import {
  getDeliveryDetailsControls,
  pruneDeliveryDetails,
} from '@sharedModule/utils/transfer-details.utils';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RecipientService } from '../../services/recipient.service';
import { RecipientListInterface } from '../../types/recipient-list';
import {
  AddRecipientInterface,
  UpdateRecipientInterface,
} from '../../types/upsert-recipient';

@Component({
  selector: 'app-upsert-recipient',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
    HasPlatformAccessDirective,
    TransferDetailsComponent,
  ],
  templateUrl: './upsert-recipient.component.html',
  styleUrl: './upsert-recipient.component.scss',
})
export class UpsertRecipientComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.recipients;
  public readonly gender = GENDER;
  upsertRecipientFrom!: FormGroup;
  isSuperAdmin!: boolean;
  partnerList!: Observable<PartnerListInterface[]>;
  senderList!: SenderListInterface[];
  beneCountry!: Observable<CountryInfo[]>;
  isEdit = false;
  recipientDetails!: RecipientListInterface;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private senderService: SenderService,
    private partnerService: PartnerService,
    private recipientService: RecipientService,
    private authenticationService: AuthenticationService,
    private messageHandlerService: MessageHandlerService
  ) {
    this.isSuperAdmin = this.authenticationService.isSuperAdmin();
  }

  ngOnInit(): void {
    this.upsertRecipientFromInit();
    if (this.isSuperAdmin) this.partnerList = this.partnerService.sharedData$;
    else this.getSenderList();
    // this.beneCountry = this.store.select(selectDestinationCountry);
    this.isEdit = this._isEdit(this.router.url);
  }

  upsertRecipientFromInit(): void {
    this.upsertRecipientFrom = new FormGroup({
      [this.attrConstant.partner.key]: new FormControl(
        '',
        this.isSuperAdmin ? [Validators.required] : []
      ),
      [this.attrConstant.sender.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.firstName.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.middleName.key]: new FormControl(''),
      [this.attrConstant.lastName.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.dob.key]: new FormControl(''),
      [this.attrConstant.gender.key]: new FormControl(''),
      [this.attrConstant.mobileCode.key]: new FormControl({
        value: '',
        disabled: true,
      }),
      [this.attrConstant.mobileNumber.key]: new FormControl('', [
        Validators.required,
        Validators.pattern(REGEX_CONSTANT.mobileNumber),
      ]),
      [this.attrConstant.email.key]: new FormControl('', [
        Validators.pattern(REGEX_CONSTANT.email),
      ]),
      [this.attrConstant.beneAddress.key]: new FormControl(''),
      [this.attrConstant.beneCity.key]: new FormControl(''),
      [this.attrConstant.benePostCode.key]: new FormControl(''),
      [this.attrConstant.beneCountry.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.senderBeneRelation.key]: new FormControl(''),
      [this.attrConstant.isFavorite.key]: new FormControl(false),
      [this.attrConstant.transferType.key]: new FormControl('', [
        Validators.required,
      ]),
      // Conditional bank-transfer / cash-pickup / mobile-transfer controls
      // (validators are toggled by app-transfer-details based on transfer type).
      ...getDeliveryDetailsControls(),
    });
  }

  onPartnerSelected(event: { value: number }): void {
    this.getSenderList(event.value);
  }

  getSenderList(partnerId?: number): void {
    this.senderService
      .getSenderDropdown(partnerId)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.senderList = x.response.map((data: SenderListInterface) => {
              return Object.assign(data, {
                sender_name: `${data.first_name} ${data.middle_name} ${data.last_name}`,
              });
            });
            if (this.isEdit && this.isSuperAdmin)
              this.onSenderSelected({
                value: this.recipientDetails.sender_id,
                selectOptions: this.senderList,
              });
          }
        },
      });
  }

  private _isEdit = (url: string): boolean => {
    const endUrl: string[] = url.split('/');
    if (endUrl[2] == 'edit') {
      this.getRecipientDetails(endUrl[3]);
      return true;
    } else {
      return false;
    }
  };

  getRecipientDetails(id: string): void {
    const payload = this.isSuperAdmin
      ? {
          bene_id: +id.split('-')[0],
          partner_id: +id.split('-')[1],
        }
      : { bene_id: +id };
    this.recipientService
      .getRecipientDetails(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            if (this.isSuperAdmin)
              this.onPartnerSelected({
                value: x.response.partner_id,
              });
            else
              this.onSenderSelected({
                value: this.recipientDetails.sender_id,
                selectOptions: this.senderList,
              });
            this.upsertRecipientFrom.patchValue(x.response);
            this.recipientDetails = x.response;
          }
        },
      });
  }

  onSenderSelected(event: {
    value: number;
    selectOptions: SenderListInterface[] | null;
  }): void {
    const selectedSender = getSenderDetailsFromId(
      event.value,
      event.selectOptions
    );
    if (selectedSender) {
      this.store.dispatch(
        loadDestinationCountry({
          payload: {
            partner_id: selectedSender.partner_id,
            src_country: selectedSender.source_country,
          },
        })
      );
      this.beneCountry = this.store.select(selectDestinationCountry);
    }
  }

  onBeneCountrySelected(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    const selectedBeneCountry = getCountryInfoFromCountryCode(
      event.value,
      event.selectOptions
    );
    if (selectedBeneCountry)
      this.upsertRecipientFrom.patchValue({
        [this.attrConstant.mobileCode.key]: selectedBeneCountry.mobileCode,
      });
  }

  onSubmit(): void {
    if (this.upsertRecipientFrom.valid) {
      const rawValue = this.upsertRecipientFrom.getRawValue();
      // Keep only the delivery-detail fields relevant to the chosen transfer
      // type so the payload matches the addBeneficiary contract.
      const formValue = pruneDeliveryDetails(
        rawValue[this.attrConstant.transferType.key],
        rawValue
      );
      const payload = this.isEdit
        ? { bene_id: this.recipientDetails.bene_id, ...formValue }
        : formValue;
      if (this.isEdit)
        this.updateRecipient(payload as unknown as UpdateRecipientInterface);
      else this.addRecipient(payload as unknown as AddRecipientInterface);
    }
  }

  addRecipient(formValue: AddRecipientInterface): void {
    this.recipientService
      .addRecipient(removeEmptyStrings(formValue))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/recipient/list']);
          }
        },
      });
  }

  updateRecipient(formValue: UpdateRecipientInterface): void {
    this.recipientService
      .updateRecipient(removeEmptyStrings(formValue))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/recipient/list']);
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
